const CheckIcon = () => (
  <svg className="h-4 w-4 shrink-0 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const RemoveIcon = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const SessionMaterialCard = ({
  session,
  material,
  onAddFiles,
  onRemoveFile,
  onCommentsChange,
  onApplyToLearner,
  learnerSessionCount,
}) => {
  const hasFiles = material.files.length > 0;
  const dateLabel = new Date(session.start).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  });
  const timeLabel = new Date(session.start).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="font-semibold text-gray-900">
            {session.learnerLabel} — {session.subjectName}
          </p>
          <p className="text-sm text-gray-600">
            {dateLabel} · {timeLabel}
          </p>
        </div>
        <span
          className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
            hasFiles ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'
          }`}
        >
          {hasFiles ? 'Materials Added' : 'No Files Uploaded Yet'}
        </span>
      </div>

      <label className="mt-4 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center hover:border-brand-medium-blue hover:bg-blue-50/40">
        <span className="text-sm font-medium text-gray-700">Drag &amp; Drop Files or Browse Files</span>
        <span className="mt-1 text-xs text-gray-500">PDF, DOC, DOCX, JPEG, PNG, GIF</span>
        <input
          type="file"
          multiple
          accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif"
          className="hidden"
          onChange={(e) => {
            onAddFiles(session.id, e.target.files);
            e.target.value = '';
          }}
        />
      </label>

      {hasFiles && (
        <ul className="mt-3 flex flex-wrap gap-2">
          {material.files.map((file) => (
            <li
              key={file.id}
              className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs text-gray-700"
            >
              <CheckIcon />
              <span className="max-w-[140px] truncate">{file.name}</span>
              <button
                type="button"
                onClick={() => onRemoveFile(session.id, file.id)}
                className="text-gray-400 hover:text-red-600"
                aria-label={`Remove ${file.name}`}
              >
                <RemoveIcon />
              </button>
            </li>
          ))}
        </ul>
      )}

      <label className="mt-4 block">
        <span className="mb-1 block text-sm font-medium text-gray-700">
          Add Comments for Tutor (optional)
        </span>
        <textarea
          value={material.comments}
          onChange={(e) => onCommentsChange(session.id, e.target.value)}
          rows={3}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand-medium-blue focus:outline-none focus:ring-2 focus:ring-brand-medium-blue/30"
          placeholder="Share context or instructions for this session"
        />
      </label>

      {hasFiles && learnerSessionCount > 1 && (
        <button
          type="button"
          onClick={() => onApplyToLearner(session)}
          className="mt-3 text-sm font-semibold text-brand-medium-blue hover:underline"
        >
          Apply these files to other sessions
        </button>
      )}
    </div>
  );
};

const BookSessionMaterialsStep = ({
  sessionsByLearner,
  activeLearner,
  onLearnerChange,
  sessionMaterials,
  onAddFiles,
  onRemoveFile,
  onCommentsChange,
  onApplyToLearner,
  onSaveContinue,
  onSkip,
}) => (
  <div className="space-y-6">
    <div>
      <h2 className="text-2xl font-bold text-gray-900">Upload Your Materials or Enter Comments</h2>
      <p className="mt-1 text-sm text-gray-600">Optional, but highly recommended for a better session.</p>
      <p className="mt-3 text-sm text-gray-700">
        Upload homework, worksheets, or notes for each session so tutors can pre-prepare.
      </p>
    </div>

    <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-1">
      {sessionsByLearner.map((group) => (
        <button
          key={group.label}
          type="button"
          onClick={() => onLearnerChange(group.label)}
          className={`rounded-t-lg px-4 py-2 text-sm font-semibold transition ${
            activeLearner === group.label
              ? 'border border-b-0 border-gray-200 bg-white text-brand-dark-blue'
              : 'text-gray-600 hover:text-brand-medium-blue'
          }`}
        >
          {group.label} ({group.sessions.length} Session{group.sessions.length !== 1 ? 's' : ''})
        </button>
      ))}
    </div>

    <div className="space-y-4">
      {sessionsByLearner
        .find((group) => group.label === activeLearner)
        ?.sessions.map((session) => (
          <SessionMaterialCard
            key={session.id}
            session={session}
            material={sessionMaterials[session.id] || { files: [], comments: '' }}
            onAddFiles={onAddFiles}
            onRemoveFile={onRemoveFile}
            onCommentsChange={onCommentsChange}
            onApplyToLearner={onApplyToLearner}
            learnerSessionCount={
              sessionsByLearner.find((group) => group.label === activeLearner)?.sessions.length || 0
            }
          />
        ))}
    </div>

    <div className="flex flex-col gap-3 sm:flex-row">
      <button
        type="button"
        onClick={onSaveContinue}
        className="rounded-lg bg-brand-medium-blue px-6 py-3 font-semibold text-white hover:bg-brand-dark-blue"
      >
        Save &amp; Continue
      </button>
      <button
        type="button"
        onClick={onSkip}
        className="rounded-lg border border-gray-300 px-6 py-3 font-semibold text-gray-700 hover:bg-gray-50"
      >
        Skip for Now
      </button>
    </div>
  </div>
);

export default BookSessionMaterialsStep;
